<template>
  <v-layout column>
    <UploadDialog
      :opened.sync="uploadDialogOpened"
      :selected-question="selectedQuestion"
      :documents-to-import="documentsToImport"
      @select-document-type="selectDocumentType"
      @pick-documents="pickDocumentsForSingleUpload()"
      @import-documents="uploadSingleFiles()"
      @add-file="addFile"
    />

    <v-dialog
      max-width="1000px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="previewDialogOpened"
      @keydown.esc="
        previewDialogOpened = false;
        documentPreviewData = '';
        previewRotation = 0;
      "
    >
      <v-card v-if="selectedDocumentLink && previewType">
        <v-card-title class="title">
          <span class="text-truncate" style="max-width: 600px">{{
            getDocumentName(selectedDocumentLink)
          }}</span>

          <v-spacer />
          <v-tooltip bottom class="mx-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="getLinkAndDownloadFile(selectedDocumentLink)"
              slot="activator"
            >
              <i
                class="material-icons-outlined primary--text"
                style="font-size: 25px"
              >
                get_app
              </i>
            </v-btn>
            <span>Download to Computer</span>
          </v-tooltip>

          <v-tooltip bottom class="mr-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="previewRotation += 90"
              slot="activator"
            >
              <v-icon size="25"> rotate_right </v-icon>
            </v-btn>
            <span>Rotate</span>
          </v-tooltip>
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0 ml-1"
            @click="
              previewDialogOpened = false;
              documentPreviewData = '';
              previewRotation = 0;
            "
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="documentPreviewData && previewLoaded">
          <preview-document
            :document-preview-data="documentPreviewData"
            :preview-type="previewType"
            :preview-rotation="previewRotation"
          />
        </v-card-text>
        <v-card-text v-else>
          <v-progress-circular
            indeterminate
            size="50"
            class="primary--text ma-5"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      max-width="800px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="selectOptionsDialogOpened"
    >
      <v-card v-if="documentUploading">
        <v-card-text>
          <v-layout column align-center justify-center class="ma-4">
            <p class="subheading">Uploading documents. Please wait...</p>
            <v-progress-circular
              indeterminate
              size="50"
              class="primary--text"
            />
          </v-layout>
        </v-card-text>
      </v-card>

      <v-card v-else-if="currentDocument">
        <v-card-title>
          <h2>Tell us about the document you just uploaded</h2>
        </v-card-title>

        <v-card-text class="text-xs-left pt-0">
          <p class="subheading">
            What is <b>{{ currentDocument.data.name }}</b> about? Please select
            all that apply.
          </p>

          <v-layout
            column
            v-for="question in allDocumentsNeeded"
            :key="question.id"
            class="option-group"
            v-if="question.options.length > 0"
          >
            <v-checkbox
              v-for="option in question.options"
              v-model="selectedOptions"
              :label="option"
              :key="option"
              :value="option"
              small
              solo
              hide-details
            />
          </v-layout>

          <v-layout column class="option-group">
            <v-checkbox
              v-model="selectedOtherOptions"
              label="Others"
              small
              solo
              hide-details
              class="mb-1"
            />

            <v-layout
              v-if="selectedOtherOptions"
              v-for="(option, index) in otherOptions"
              :key="option"
              align-center
              class="mt-2"
            >
              <v-text-field
                class="mx-4 my-0 default-input"
                label="Enter document name"
                v-model="otherOptions[index]"
                solo
                hide-details
              />

              <v-btn
                flat
                icon
                color="error"
                class="my-0"
                @click="deleteOtherOption(index)"
              >
                <v-icon class="material-icons-outlined">delete</v-icon>
              </v-btn>
            </v-layout>

            <v-layout v-if="selectedOtherOptions" class="mt-1">
              <v-btn
                outline
                color="primary"
                small
                class="mx-4 text-none"
                @click="addMoreOptions()"
              >
                <v-icon>add</v-icon>
                Add more row
              </v-btn>
            </v-layout>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            class="error--text text-none"
            flat
            @click="selectOptionsDialogOpened = false"
          >
            Cancel
          </v-btn>

          <v-btn
            class="primary text-none"
            @click="eventEmitter.emit('selected-options')"
            :disabled="!canInsert"
          >
            Done
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmationDialog
      v-model="deleteAllDocumentDialogOpened"
      @confirm="deleteQuestionDocuments()"
      @cancel="closedDeleteDocumentsConfirmationDialog()"
    >
      {{ $t('questionnaire.removeAllUploadedDocuments') }}
    </ConfirmationDialog>

    <input
      type="file"
      style="display: none"
      ref="selectedDocumentsForBulkUpload"
      multiple
      @change="handleDocumentsPickedForBulkUpload"
    />

    <input
      type="file"
      style="display: none"
      ref="selectedDocumentsForSingleUpload"
      multiple
      @change="handleDocumentsPickedForSingleUpload"
    />

    <v-layout column align-center v-if="checklistLoading">
      <v-progress-circular
        indeterminate
        size="40"
        class="ma-5"
        color="primary"
      ></v-progress-circular>
    </v-layout>

    <v-layout column v-else class="mb-2 subheading mt-2">
      <h2 class="section-title text-xs-left mt-1 mb-2" v-if="!hideList">
        Document checklist
      </h2>

      <span
        v-if="isEmpty(allDocumentsNeeded) && !hideList"
        class="darkgrey--text body-2 text-xs-left"
        >Documents were not requested for the questionnaire.</span
      >

      <v-expansion-panel class="my-2 expansion-panel" expand>
        <v-expansion-panel-content
          v-for="question in allDocumentsNeeded"
          :key="question.id"
          :class="
            showUnanswered && isEmpty(question.answer) ? 'outlined-red' : ''
          "
          :value="showUnanswered && isEmpty(question.answer) ? true : undefined"
        >
          <template v-slot:header>
            <v-layout row align-center>
              <div class="pr-2">
                <v-icon
                  v-if="question.answer_by && !question.answer"
                  large
                  class="darkgrey--text text--lighten-2"
                >
                  watch_later
                </v-icon>

                <v-icon
                  v-else-if="!isEmpty(question.answer)"
                  large
                  color="success"
                >
                  check_circle
                </v-icon>

                <v-icon v-else large class="darkgrey--text text--lighten-2"
                  >radio_button_unchecked</v-icon
                >
              </div>

              <div
                class="document-item-text"
                v-html="
                  !isEmpty(question.options)
                    ? question.options.join(', ')
                    : question.text
                "
              />
            </v-layout>
          </template>

          <v-card>
            <v-card-text class="pt-0">
              <v-layout column class="px-2">
                <v-layout align-center v-if="question.answer_by">
                  <span class="darkgrey--text text-xs-left subheading"
                    >The client wants to upload this item after
                    {{ moment(question.answer_by).format('ll') }}.
                    <v-btn
                      v-if="listDocuments(question.answer).length == 0"
                      @click="saveQuestion(question, '')"
                      outline
                      small
                      color="primary"
                      class="ml-3 font-weight-bold text-capitalize"
                      >Undo</v-btn
                    >
                  </span>
                </v-layout>

                <v-layout
                  align-center
                  v-else-if="question.answer === 'Not Applicable'"
                >
                  <span class="darkgrey--text text-xs-left subheading"
                    >This item was marked <strong>not applicable</strong>.</span
                  >
                  <v-btn
                    @click="saveQuestion(question, '')"
                    outline
                    small
                    color="primary"
                    class="ml-3 font-weight-bold text-capitalize"
                    >Undo</v-btn
                  >
                </v-layout>

                <v-layout
                  align-center
                  v-else-if="question.answer === 'Marked Completed'"
                >
                  <span class="darkgrey--text text-xs-left subheading"
                    >This item was marked <b>completed</b>.</span
                  >
                  <v-btn
                    @click="saveQuestion(question, '')"
                    outline
                    small
                    color="primary"
                    class="ml-3 font-weight-bold text-capitalize"
                    >Undo</v-btn
                  >
                </v-layout>

                <v-layout
                  align-center
                  v-else-if="question.answer === 'Provided in Questionnaire'"
                >
                  <span class="darkgrey--text text-xs-left subheading"
                    >This item was marked provided in the questionnaire.</span
                  >
                  <v-btn
                    @click="saveQuestion(question, '')"
                    outline
                    small
                    color="primary"
                    class="ml-3 font-weight-bold text-capitalize"
                    >Undo</v-btn
                  >
                </v-layout>

                <v-layout
                  align-center
                  v-else-if="question.answer === 'Provided Elsewhere'"
                >
                  <span class="darkgrey--text text-xs-left subheading"
                    >This item was marked <b>provided elsewhere</b>.</span
                  >
                  <v-btn
                    @click="saveQuestion(question, '')"
                    outline
                    small
                    color="primary"
                    class="ml-3 font-weight-bold text-capitalize"
                    >Undo</v-btn
                  >
                </v-layout>

                <div v-else class="description mb-2 darkgrey--text">
                  <div
                    v-if="
                      question.section === 'Documents from Last Year' ||
                        question.section === 'Other Prior Year Documents'
                    "
                  >
                    <span
                      >This item is listed because the client uploaded the same
                      document last year.</span
                    >
                  </div>
                  <div v-else-if="question.follow_up_question_id">
                    <span
                      v-html="
                        formatReason(
                          question,
                          getParentQuestion(findSection(question), question),
                        )
                      "
                    />
                  </div>
                  <div
                    v-else-if="question.filtered_questionnaire_comments.length"
                  >
                    <v-chip
                      class="clickable-chip"
                      small
                      @click="$emit('open-comment-dialog', question)"
                      >{{
                        question.filtered_questionnaire_comments.length
                      }}
                      Note{{
                        question.filtered_questionnaire_comments.length > 1
                          ? 's'
                          : ''
                      }}</v-chip
                    >
                    <span>Click to view notes</span>
                  </div>
                </div>
              </v-layout>

              <draggable
                :value="listDocuments(question.answer)"
                :animation="200"
                ghost-class="ghost-card"
                group="documents"
                :data-question-id="question.id"
                @end="reorderDocuments"
              >
                <v-layout
                  v-if="!isEmpty(question.answer)"
                  align-center
                  class="file-item mx-2 mb-2 px-3 py-2 text-xs-left body-1"
                  v-for="(document, i) in listDocuments(question.answer)"
                  :key="i"
                  @click="previewDocument(document)"
                >
                  <v-icon
                    color="grey darken-2"
                    class="mr-1 material-icons-outlined move-handle"
                  >
                    drag_handle
                  </v-icon>

                  <v-icon
                    color="grey darken-2"
                    class="mr-1 material-icons-outlined"
                  >
                    insert_drive_file
                  </v-icon>
                  <div class="mr-1">{{ getDocumentName(document) }}</div>
                  <v-layout
                    align-start
                    wrap
                    v-if="findExtractedDocument(document)"
                  >
                    <template
                      v-for="recognizedDocument in childDocuments(
                        findExtractedDocument(document),
                      )"
                    >
                      <v-chip
                        small
                        v-if="!isEmpty(recognizedDocument.detailed_name)"
                        >{{ recognizedDocument.detailed_name }}</v-chip
                      >
                    </template>
                  </v-layout>
                  <v-spacer />
                  <v-btn
                    @click.stop.prevent="deleteDocument(question, document)"
                    style="cursor: pointer"
                    color="error"
                    flat
                    small
                    class="text-none"
                    :loading="isDocumentBeingDeleted(question.id, document)"
                  >
                    Remove
                  </v-btn>
                </v-layout>
              </draggable>

              <v-layout
                wrap
                class="pt-2"
                v-if="
                  question.answer !== 'Upload Later' &&
                    question.answer !== 'Answer Later' &&
                    question.answer !== 'Not Applicable' &&
                    question.answer !== 'Provided Elsewhere' &&
                    question.answer !== 'Marked Completed'
                "
              >
                <v-btn
                  color="primary"
                  class="text-none"
                  @click="openUploadDialog(question)"
                  :loading="questionsUploadingDocs.includes(question.id)"
                >
                  Upload
                </v-btn>

                <v-btn
                  v-if="isEmpty(question.answer)"
                  outline
                  color="primary"
                  class="text-none"
                  @click="saveQuestion(question, 'Marked Completed')"
                >
                  Marked Completed
                </v-btn>

                <v-btn
                  outline
                  color="primary"
                  class="text-none"
                  @click="
                    checkIfOpenDeleteDocumentConfirmationDialog(question)
                      ? openDeleteDocumentsConfirmationDialog(
                          question,
                          'Not Applicable',
                        )
                      : saveQuestion(question, 'Not Applicable')
                  "
                >
                  Not Applicable
                </v-btn>

                <!--
                <v-btn
                  v-if="!question.disable_answer_later"
                  outline
                  color="primary"
                  class="text-none"
                  @click="saveQuestion(question, 'Upload Later')"
                >
                  Upload Later
                </v-btn>
                -->

                <v-btn
                  v-if="!disableProvidedElsewhere"
                  outline
                  color="primary"
                  class="text-none"
                  @click="
                    checkIfOpenDeleteDocumentConfirmationDialog(question)
                      ? openDeleteDocumentsConfirmationDialog(
                          question,
                          'Provided Elsewhere',
                        )
                      : saveQuestion(question, 'Provided Elsewhere')
                  "
                >
                  Provided Elsewhere
                </v-btn>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>

        <v-expansion-panel-content :value="otherDocumentsTab">
          <template v-slot:header>
            <v-layout row align-center>
              <div class="pr-2">
                <v-icon
                  large
                  class="darkgrey--text text--lighten-1 material-icons-outlined"
                  >file_copy</v-icon
                >
              </div>
              <span class="document-item-text">Additional Attachments</span>
              <v-chip class="ml-2" small>
                {{
                  currentQuestionnaireOtherDocuments.length === 1
                    ? '1 document'
                    : currentQuestionnaireOtherDocuments.length + ' documents'
                }}
              </v-chip>
            </v-layout>
          </template>

          <v-card>
            <v-card-text class="pt-0">
              <draggable
                :value="currentQuestionnaireOtherDocuments"
                :animation="200"
                ghost-class="ghost-card"
                group="documents"
                @end="reorderDocuments"
              >
                <v-layout
                  align-center
                  class="file-item mx-2 mb-2 px-3 py-2 text-xs-left body-1"
                  v-for="(document, i) in currentQuestionnaireOtherDocuments"
                  :key="document.id"
                  @click="previewDocument(document)"
                >
                  <v-icon
                    color="grey darken-2"
                    class="mr-1 material-icons-outlined move-handle"
                  >
                    drag_handle
                  </v-icon>

                  <v-icon
                    color="grey darken-2"
                    class="mr-1 material-icons-outlined"
                  >
                    insert_drive_file
                  </v-icon>
                  {{ getDocumentName(document) }}
                  <v-spacer />

                  <v-btn
                    @click.stop.prevent="deleteDocument(null, document)"
                    style="cursor: pointer"
                    color="error"
                    flat
                    small
                    class="text-none"
                    :loading="isDocumentBeingDeleted('other', document)"
                  >
                    Remove
                  </v-btn>
                </v-layout>

                <v-layout wrap class="pt-2">
                  <v-btn
                    color="primary"
                    class="text-none"
                    @click="pickDocumentsForBulkUpload()"
                  >
                    Upload
                  </v-btn>
                </v-layout>
              </draggable>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-layout>
  </v-layout>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';
import get from 'lodash/get';
import API from '@/api';
import moment from 'moment';
import PreviewDocument from '@/components/PreviewDocument';
import EventEmitter from 'eventemitter3';
import UploadDialog from '@/components/dialogs/UploadDialog';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import UtilsMixins from '@/mixins/UtilsMixins';
import NewDocumentChecklistMixin from '@/mixins/NewDocumentChecklistMixin';
import QuestionnaireMixin from '../mixins/QuestionnaireMixin';
import DocumentMixin from '../mixins/DocumentMixin';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';
import draggable from 'vuedraggable';

export default {
  components: {
    PreviewDocument,
    UploadDialog,
    ConfirmationDialog,
    draggable,
  },

  mixins: [
    UtilsMixins,
    NewDocumentChecklistMixin,
    QuestionnaireMixin,
    DocumentMixin,
    FirmPreferenceMixins,
  ],

  props: {
    currentCompany: {
      type: Object,
    },

    checklistLoading: {
      type: Boolean,
    },

    currentQuestionnaire: {
      type: Object,
      default: null,
    },

    showUnanswered: {
      type: Boolean,
      default: false,
    },

    hideList: {
      optional: true,
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // For single-file upload dialog
      uploadDialogOpened: false,
      selectedQuestion: null,
      selectedDocumentType: '',
      documentsToImport: [],
      questionsUploadingDocs: [],
      deleteAllDocumentDialogOpened: false,
      currentQuestion: null,
      currentProgress: '',
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
    }),
  },

  methods: {
    isEmpty,
    moment,

    async reorderDocuments(event) {
      const sourceQuestionId = event.from.hasAttribute('data-question-id')
        ? parseInt(event.from.getAttribute('data-question-id'))
        : null;

      const targetQuestionId = event.to.hasAttribute('data-question-id')
        ? parseInt(event.to.getAttribute('data-question-id'))
        : null;

      const oldIndex = event.oldIndex;
      const newIndex = event.newIndex;

      const sourceQuestion = sourceQuestionId
        ? this.getQuestionById(sourceQuestionId)
        : null;
      const targetQuestion = targetQuestionId
        ? this.getQuestionById(targetQuestionId)
        : null;

      if (!sourceQuestionId && !targetQuestionId) {
        // Case 0: Reordering documents within Additional Documents section
        // Currently, we don't need to support reordering documents in Additional Documents section
        // so we do nothing here
      }
      // Case 1: Moving a document from Additional Documents section to a upload question
      else if (!sourceQuestionId) {
        const targetDocument = this.currentQuestionnaireOtherDocuments[
          oldIndex
        ];

        const targetQuestionDocuments = this.listDocuments(
          targetQuestion.answer,
        );
        targetQuestionDocuments.splice(newIndex, 0, targetDocument);
        targetQuestion.answer = '&!&' + targetQuestionDocuments.join('&!&');

        const extractedDocument = this.currentQuestionnaire.extracted_documents.find(
          d => d.source.endsWith(targetDocument),
        );

        extractedDocument.marked_as_others = false;

        await Promise.all([
          API.questionnaire.answerQuestion({
            id: targetQuestion.id,
            answer: targetQuestion.answer,
            companyId: this.currentCompany.id,
            answerBy: targetQuestion.answer_by,
          }),
          API.extractedDocument.updateMarkedAsOthers({
            extractedDocumentId: extractedDocument.id,
            companyId: this.currentCompany.id,
            markedAsOthers: extractedDocument.marked_as_others,
          }),
        ]);
      }
      // Case 2: Moving a document from a upload question to Additional Documents section
      else if (!targetQuestionId) {
        const targetDocument = this.listDocuments(sourceQuestion.answer)[
          oldIndex
        ];

        const sourceQuestionDocuments = this.listDocuments(
          sourceQuestion.answer,
        ).filter(d => d !== targetDocument);

        sourceQuestion.answer =
          sourceQuestionDocuments.length === 0
            ? ''
            : '&!&' + sourceQuestionDocuments.join('&!&');

        const extractedDocument = this.currentQuestionnaire.extracted_documents.find(
          d => d.source.endsWith(targetDocument),
        );

        extractedDocument.marked_as_others = true;

        await Promise.all([
          API.questionnaire.answerQuestion({
            id: sourceQuestion.id,
            answer: sourceQuestion.answer,
            companyId: this.currentCompany.id,
            answerBy: sourceQuestion.answer_by,
          }),
          API.extractedDocument.updateMarkedAsOthers({
            extractedDocumentId: extractedDocument.id,
            companyId: this.currentCompany.id,
            markedAsOthers: extractedDocument.marked_as_others,
          }),
        ]);
      }
      // Case 3: Moving a document within the same upload question
      else if (sourceQuestionId === targetQuestionId) {
        const targetDocument = this.listDocuments(sourceQuestion.answer)[
          oldIndex
        ];

        const sourceQuestionDocuments = this.listDocuments(
          sourceQuestion.answer,
        );
        sourceQuestionDocuments.splice(oldIndex, 1);
        sourceQuestionDocuments.splice(newIndex, 0, targetDocument);
        sourceQuestion.answer = '&!&' + sourceQuestionDocuments.join('&!&');

        await API.questionnaire.answerQuestion({
          id: sourceQuestion.id,
          answer: sourceQuestion.answer,
          companyId: this.currentCompany.id,
          answerBy: sourceQuestion.answer_by,
        });
      }
      // Case 4: Moving a document from an upload question to another question
      else {
        const targetDocument = this.listDocuments(sourceQuestion.answer)[
          oldIndex
        ];

        const sourceQuestionDocuments = this.listDocuments(
          sourceQuestion.answer,
        ).filter(d => d !== targetDocument);

        sourceQuestion.answer =
          sourceQuestionDocuments.length === 0
            ? ''
            : '&!&' + sourceQuestionDocuments.join('&!&');

        const targetQuestionDocuments = this.listDocuments(
          targetQuestion.answer,
        );
        targetQuestionDocuments.splice(newIndex, 0, targetDocument);
        targetQuestion.answer = '&!&' + targetQuestionDocuments.join('&!&');

        await Promise.all([
          API.questionnaire.answerQuestion({
            id: sourceQuestion.id,
            answer: sourceQuestion.answer,
            companyId: this.currentCompany.id,
            answerBy: sourceQuestion.answer_by,
          }),
          API.questionnaire.answerQuestion({
            id: targetQuestion.id,
            answer: targetQuestion.answer,
            companyId: this.currentCompany.id,
            answerBy: targetQuestion.answer_by,
          }),
        ]);
      }
    },

    formatReason(question, parentQuestion) {
      return `This item is listed because the client answered <b>'${question.trigger_for_follow_up_question}</b>' to<br/>'<b>${parentQuestion.text}</b>'`;
    },

    pickDocumentsForSingleUpload() {
      const input = this.$refs.selectedDocumentsForSingleUpload;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocumentsForSingleUpload.click();
    },

    pickDocumentsForBulkUpload() {
      const input = this.$refs.selectedDocumentsForBulkUpload;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocumentsForBulkUpload.click();
    },

    selectDocumentType(documentType) {
      this.selectedDocumentType = documentType;
    },

    openUploadDialog(question) {
      if (!question) {
        this.uploadDialogOpened = true;
      }
      if (!this.questionHasDocumentsLoading(question.id)) {
        this.selectedQuestion = question;
        this.documentsToImport = [];
        this.selectedDocumentType =
          this.selectedQuestion.options.length > 1
            ? ''
            : this.selectedQuestion.options[0];
        this.uploadDialogOpened = true;
      }
    },

    questionHasDocumentsLoading(questionId) {
      return this.questionsUploadingDocs.includes(questionId);
    },

    async addFile(e) {
      const documents = e.dataTransfer.files;
      if (documents) {
        const documentType = this.selectedDocumentType;
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name:
                documentType && !this.newDocumentManagementSystem
                  ? `${this.getNameWithoutExtensionOfFilename(
                      document.name,
                    )} (${documentType})`
                  : this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
              documentType,
            };
          }),
        );
        this.uploadBulkDocument();
      }
    },

    async handleDocumentsPickedForSingleUpload(e) {
      const documents = e.target.files;
      if (documents) {
        const documentType = this.selectedDocumentType;
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name:
                documentType && !this.newDocumentManagementSystem
                  ? `${this.getNameWithoutExtensionOfFilename(
                      document.name,
                    )} (${documentType})`
                  : this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
              documentType,
            };
          }),
        );
      }
    },

    async uploadSingleFiles(e) {
      const questionId = this.selectedQuestion.id;
      this.questionsUploadingDocs.push(questionId);

      let path;
      let answer =
        this.selectedQuestion.answer === 'Not Applicable' ||
        isEmpty(this.selectedQuestion.answer)
          ? ''
          : this.selectedQuestion.answer;
      if (isEmpty(this.currentQuestionnaire.document_path)) {
        path = '';
      } else {
        path = this.currentQuestionnaire.document_path
          .replace('{year}', this.currentQuestionnaire.tax_year)
          .replace('{questionnaire_name}', this.currentQuestionnaire.name);
      }
      try {
        this.uploadDialogOpened = false;

        for (const documentToImport of this.documentsToImport) {
          const trueExtension = await this.getTrueExtension(
            documentToImport.data,
          );
          const extensionsToCheck = ['pdf', 'png', 'gif', 'jpeg'];

          if (documentToImport.data.size === 0) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be EMPTY. Kindly check those files and try uploading them again.`,
            );
          }

          if (
            extensionsToCheck.includes(documentToImport.extension) &&
            trueExtension !== documentToImport.extension
          ) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.`,
            );
          }

          if (documentToImport.extension === 'pdf') {
            if (await this.pdfHasPassword(documentToImport.data)) {
              throw new Error(
                `The PDFs you uploaded are password-protected. Please either remove the password or upload alternative files.`,
              );
            }
          }

          const {
            data: { id, upload_link },
          } = await API.integration.getTemporaryUploadLink({
            companyId: this.currentCompany.id,
          });

          await API.integration.uploadToDropbox(
            upload_link,
            documentToImport.data,
          );

          const response = await API.integration.processDocument({
            id,
            documentName:
              documentToImport.name + '.' + documentToImport.extension,
            companyId: this.currentCompany.id,
            path: '/'.concat(path),
            questionnaireId: this.currentQuestionnaire.id,
            selectedOptions: documentToImport.documentType
              ? [documentToImport.documentType]
              : [],
            description: documentToImport.documentType
              ? documentToImport.documentType
              : '',
            tags: [this.selectedQuestion.section],
          });

          if (response.data.error) {
            console.log(response.data.error);
          } else {
            let list = response.data.document_location.split('/');
            list.splice(0, 3);
            answer += '&!&'.concat(list.join('/'));
          }
        }

        const question = this.currentQuestionnaire.questionnaire_questions.find(
          question => question.id === questionId,
        );
        question.answer = answer;

        this.documentsToImport = [];

        await API.questionnaire.answerQuestion({
          id: questionId,
          answer: answer,
          companyId: this.currentCompany.id,
        });

        this.$emit('refresh-questionnaire-until-documents-are-processed');
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: has(e, 'message')
            ? get(e, 'message')
            : 'There was a problem uploading the documents.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.markQuestionDocUploaded(questionId);
      }
    },

    async handleDocumentsPickedForBulkUpload(e) {
      const documents = e.target.files;

      if (documents) {
        this.documentsToImport.push(
          ...Array.from(documents).map(document => {
            return {
              name: this.getNameWithoutExtensionOfFilename(document.name),
              data: document,
              extension: this.getExtensionOfFilename(document.name),
            };
          }),
        );

        this.uploadBulkDocument();
      }
    },

    markQuestionDocUploaded(questionId) {
      this.questionsUploadingDocs = this.questionsUploadingDocs.filter(
        id => id !== questionId,
      );
    },

    async getLinkAndDownloadFile(source) {
      try {
        const response = await API.integration.downloadDocument({
          companyId: this.currentCompany.id,
          link: this.currentCompany.upload_link.concat('/', source),
        });

        this.downloadDocumentByLink(
          response.data.document.data.link,
          this.getDocumentName(source),
        );
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
        });
      }
    },
    checkIfOpenDeleteDocumentConfirmationDialog(question) {
      console.log('check if open', question);
      if (
        !['Not Applicable', 'Provided Elsewhere', ''].includes(question.answer)
      ) {
        console.log('open dialog');
        return true;
      }
      return false;
    },
    openDeleteDocumentsConfirmationDialog(question, action) {
      this.deleteAllDocumentDialogOpened = true;
      this.currentQuestion = question;
      this.currentProgress = action;
    },

    deleteQuestionDocuments() {
      console.log('delete document');
      this.saveQuestion(this.currentQuestion, this.currentProgress);
      this.closedDeleteDocumentsConfirmationDialog();
    },

    closedDeleteDocumentsConfirmationDialog() {
      this.deleteAllDocumentDialogOpened = false;
      this.currentQuestion = null;
      this.currentProgress = '';
    },
  },
  created() {
    this.$emit('refresh-questionnaire-until-documents-are-processed');
  },
};
</script>

<style lang="postcss" scoped>
.section-title {
  font-weight: 700;
  font-size: 20px;
}
.option-group {
  padding: 0px 10px 12px;
  margin: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);

  & >>> .v-input {
    margin-top: 12px;
  }
}
.file-item {
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  background: #fff;
  cursor: pointer;

  &:hover {
    background: #f1f1f1;
  }
}

.document-item {
  padding: 5px 0px;
}
.expansion-panel {
  border: none;
  box-shadow: none;

  & >>> .v-expansion-panel__container {
    border: 1px solid rgba(0, 0, 0, 0.2) !important;
    border-radius: 4px;
    margin-bottom: 12px;
    overflow: hidden;
  }

  & >>> .v-expansion-panel__container.outlined-red {
    border: 1px solid red !important;
  }
}

.description {
  font-size: 15px;
}

.document-item-text {
  font-size: 16px;

  & >>> p {
    margin-bottom: 8px;
  }
}

.select-option {
  & >>> .v-input__control .v-input__slot {
    min-height: 46px;
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-raidus: 4px;
    font-size: 15px;
  }
}
.why-text {
  font-size: 12px;
}
.action-button {
  & >>> .v-btn__content {
    white-space: normal;
  }
}
.drag-and-drop {
  height: 140px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);

  & >>> i {
    color: rgba(0, 0, 0, 0.3);
  }

  & >>> span {
    color: rgba(0, 0, 0, 0.6);
  }

  & >>> span.clickable {
    cursor: pointer;
  }
}
.clickable-chip {
  & >>> .v-chip__content {
    cursor: pointer !important;
  }
}
.outlined-red {
  border: 1px solid #cb4039;
}
.drag-and-drop-text {
  font-size: 14px;
}
.max-size-text {
  font-size: 14px;
}
.move-handle {
  cursor: move;
}
</style>
